/**
 * DeliveryEstimateContext
 *
 * Provides a SINGLE, app-wide delivery estimate that is fetched once
 * for all visible shops whenever the user's location changes.
 *
 * Any component can call:
 *   const { formatETA } = useDeliveryEstimateContext();
 *
 * Instead of creating N separate hooks in N components (the root cause of
 * all cards showing the same delivery time).
 */
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import DeliveryEstimateServ from '../service/DeliveryEstimateServ';
import dayjs from 'dayjs';

const DeliveryEstimateContext = createContext({
    registerShop: () => {},
    formatETA: (_shopId, fallback = null) => fallback,
    loading: false
});

export const useDeliveryEstimateContext = () => useContext(DeliveryEstimateContext);

/**
 * Wrap your app (or App.js) with this provider once.
 * It batches all shop registrations within a tick and fires one API call.
 */
export const DeliveryEstimateProvider = ({ children, latitude, longitude }) => {
    const [estimates, setEstimates] = useState({});   // key: shopId (lowercase)
    const [loading, setLoading] = useState(false);

    // Pending shop IDs waiting to be fetched
    const pendingRef   = useRef(new Set());
    const fetchTimerRef = useRef(null);

    /**
     * Register a shopId for estimation.
     * Multiple calls within 80ms are batched into a single API request.
     */
    const registerShop = useCallback((shopId) => {
        if (!shopId || !latitude || !longitude) return;
        const sIdStr = shopId.toString().toLowerCase();

        // Already fetched — no need to refetch
        if (estimates[sIdStr] !== undefined) return;

        pendingRef.current.add(sIdStr);

        // Debounce: wait 80ms to collect more shopIds before firing
        if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
        fetchTimerRef.current = setTimeout(async () => {
            const idsToFetch = [...pendingRef.current];
            if (idsToFetch.length === 0) return;
            pendingRef.current.clear();

            try {
                setLoading(true);
                const res = await DeliveryEstimateServ.getEstimate({
                    shopId: idsToFetch,
                    latitude,
                    longitude
                });

                if (res?.data) {
                    setEstimates(prev => {
                        const updated = { ...prev };
                        res.data.forEach(item => {
                            updated[item.shopId?.toString().toLowerCase()] = item;
                        });
                        return updated;
                    });
                }
            } catch (err) {
                console.error('[DeliveryEstimateContext] fetch error:', err);
            } finally {
                setLoading(false);
            }
        }, 80);
    }, [latitude, longitude, estimates]);

    /**
     * Format delivery time for a given shopId.
     * Call registerShop(shopId) first so the data is fetched.
     */
    const formatETA = useCallback((shopId, fallbackEta = null) => {
        if (!shopId) return fallbackEta;
        const sIdStr = shopId.toString().toLowerCase();
        const est = estimates[sIdStr];

        if (!est) {
            return loading ? 'Estimating...' : fallbackEta;
        }

        if (est.status === 'not_available' || est.shopStatus === 'closed') {
            return fallbackEta;
        }

        // 🚚 Precise Dynamic ETA from backend
        if (est.estimatedDeliveryTime) {
            return est.estimatedDeliveryTime;
        }

        // deliveryTime or deliveryMinutes field (numeric minutes)
        const dt = est.deliveryMinutes || est.deliveryTime;
        if (dt !== undefined && dt !== null) {
            if (typeof dt === 'number') {
                return dt >= 60
                    ? `${Math.floor(dt / 60)} hr${Math.floor(dt / 60) > 1 ? 's' : ''}${dt % 60 > 0 ? ` ${dt % 60} min` : ''}`
                    : `${dt} min`;
            }
            return dt; // already formatted string
        }

        // durationMin field (from old calculateETA response)
        if (est.durationMin) {
            const dm = est.durationMin;
            return dm >= 60
                ? `${Math.round(dm / 60)} hr${Math.round(dm / 60) > 1 ? 's' : ''}`
                : `${dm} min`;
        }

        // Fall back to eta ISO string → calculate minutes from now
        if (est.eta) {
            const diffMinutes = Math.max(1, dayjs(est.eta).diff(dayjs(), 'minute'));
            if (diffMinutes >= 1440) {
                const days = Math.round(diffMinutes / 1440);
                return `${days} day${days > 1 ? 's' : ''}`;
            } else if (diffMinutes >= 60) {
                const hours = Math.round(diffMinutes / 60);
                return `${hours} hr${hours > 1 ? 's' : ''}`;
            }
            return `${diffMinutes} min`;
        }

        return fallbackEta;
    }, [estimates, loading]);

    return (
        <DeliveryEstimateContext.Provider value={{ registerShop, formatETA, loading, estimates }}>
            {children}
        </DeliveryEstimateContext.Provider>
    );
};
